import {
  EditIcon,
  IconButtonColorEnum,
  IconButtonSection,
  IconButtonSectionInternalProps,
  IconButtonSectionSizeEnum,
  ImageThumbnail,
  ImageThumbnailSizesEnum,
  TextOrientationEnum,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { CopyTextButton } from '@/components/CopyTextButton/CopyTextButton';
import { ItemList } from '@/components/ItemList';
import { StateActiveInTable } from '@/components/StateActiveInTable/StateActiveInTable';
import { UserAction } from '@/components/UserAction';
import { useAudienceChannelSources } from '@/features/broadcasters/api/get-audience-channel-sources';
import { useEconomicalValueChannelSources } from '@/features/broadcasters/api/get-economical-value-channel-sources';
import { CHANNEL_MEDIA_TYPE_TO_ICON } from '@/features/broadcasters/constants/icons';
import { ChannelType } from '@/features/broadcasters/types';
import { useLocationCodeToLabel } from '@/features/locations/hooks/useGetLocationLabelFromCode';

const internalPropsIconButtonEditUser: IconButtonSectionInternalProps = {
  icon: {
    type: EditIcon,
  },
  iconButton: {
    color: IconButtonColorEnum.NONE_DEFAULT,
  },
  text: {
    value: 'Editar',
    orientation: TextOrientationEnum.LEFT,
    className: 'text-text-dark-10 text-body-3 font-regular',
  },
};

type ParentBroadcasteretailsProps = {
  parentBroadcaster: ChannelType;
  onUpdate: (parentBroadcaster: ChannelType) => void;
};

export function ParentBroadcastersDetail({
  parentBroadcaster,
  onUpdate,
}: ParentBroadcasteretailsProps) {
  const { t } = useTranslation();
  const ChannelMediaTypeIcon = CHANNEL_MEDIA_TYPE_TO_ICON[parentBroadcaster.media_type];
  const locationCodeToLabel = useLocationCodeToLabel();

  const economicalValueId = parentBroadcaster.economical_value_channel_source_id || null;
  const { data: economicalValueChannelSources = [] } = useEconomicalValueChannelSources({
    params: economicalValueId ? { idIn: [economicalValueId] } : undefined,
    config: { enabled: Boolean(economicalValueId) },
  });

  const audienceId = parentBroadcaster.audience_channel_source_id || null;
  const { data: audienceChannelSources = [] } = useAudienceChannelSources({
    params: audienceId ? { idIn: [audienceId] } : undefined,
    config: { enabled: Boolean(audienceId) },
  });

  const audienceChannelSourceId = audienceChannelSources.find(
    audienceChannelSource =>
      audienceChannelSource.id === parentBroadcaster.audience_channel_source_id
  );
  const economicalValueChannelSourceId = economicalValueChannelSources.find(
    economicalValueChannelSource =>
      economicalValueChannelSource.id === parentBroadcaster.economical_value_channel_source_id
  );

  return (
    <div className="flex flex-1 flex-col items-start self-stretch">
      <div className="flex items-start justify-end self-stretch">
        <IconButtonSection
          onClick={() => onUpdate(parentBroadcaster)}
          internalProps={internalPropsIconButtonEditUser}
          size={IconButtonSectionSizeEnum.LG}
        />
      </div>
      <div className="flex w-full flex-col divide-y divide-stroke-20">
        <ItemList title={t('Logo')}>
          <ImageThumbnail
            src={parentBroadcaster.parameters?.logo_presigned_url}
            size={ImageThumbnailSizesEnum.Xxl}
          />
        </ItemList>
        <ItemList title={t('Nombre corto')} contentIcon={ChannelMediaTypeIcon}>
          {parentBroadcaster.name}
        </ItemList>
        <ItemList title={t('ID AV (Legacy)')}> {parentBroadcaster.av_code} </ItemList>
        <ItemList title={t('Ámbito')}> {parentBroadcaster.scope} </ItemList>
        <ItemList title={t('País')}>
          {' '}
          {locationCodeToLabel[parentBroadcaster.country_code]}{' '}
        </ItemList>
        <ItemList title={t('Región')}>
          {parentBroadcaster.region_code && locationCodeToLabel[parentBroadcaster.region_code]}
        </ItemList>
        <ItemList title={t('Subregión')}>
          {parentBroadcaster.subregion_code &&
            locationCodeToLabel[parentBroadcaster.subregion_code]}
        </ItemList>
        <ItemList title={t('Estado')}>
          <StateActiveInTable status={parentBroadcaster.state} />
        </ItemList>
        <ItemList title={t('ID Audiencias')}>{audienceChannelSourceId?.name}</ItemList>
        <ItemList title={t('ID Tarifas')}>{economicalValueChannelSourceId?.name}</ItemList>
        <ItemList title={t('URL de stream')}>
          <div className="flex items-center gap-2">
            {parentBroadcaster.uri}

            {parentBroadcaster.uri && (
              <CopyTextButton content={parentBroadcaster.uri} message="URL de stream copiada" />
            )}
          </div>
        </ItemList>
        <ItemList title={t('Creado por')}>
          <UserAction
            id={parentBroadcaster?.created_by || new Date().toISOString()}
            timestamp={parentBroadcaster?.created_at || new Date().toISOString()}
          />
        </ItemList>
        <ItemList title={t('Actualizado por')}>
          <UserAction
            id={parentBroadcaster?.updated_by || new Date().toISOString()}
            timestamp={parentBroadcaster?.updated_at || new Date().toISOString()}
          />
        </ItemList>
      </div>
    </div>
  );
}
